import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  Clock,
  Lightbulb,
  MessageSquarePlus,
  ChevronUp,
  Loader2,
  X,
  Sparkles,
  Rocket,
  Target,
  Zap,
  Bug,
  Puzzle,
  Send,
  AlertTriangle,
  Upload,
  Image as ImageIcon,
  Trash2,
} from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const API_URL = import.meta.env.VITE_API_URL || 'https://sweetpilot.com/api';

interface RoadmapItem {
  id: number;
  title: string;
  description: string | null;
  status: 'completed' | 'in_progress' | 'planned' | 'consideration';
  category: 'feature' | 'improvement' | 'bug_fix' | 'integration';
  votes_count: number;
  priority: number;
  completed_at: string | null;
  created_at: string;
}

interface GroupedRoadmap {
  in_progress: RoadmapItem[];
  planned: RoadmapItem[];
  consideration: RoadmapItem[];
  completed: RoadmapItem[];
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

const statusConfig = {
  in_progress: {
    label: 'In Progress',
    icon: Rocket,
    color: 'from-accent-cyan to-accent-blue',
    bgColor: 'bg-accent-cyan/10',
    lightBgColor: 'bg-cyan-50',
    borderColor: 'border-accent-cyan/30',
    lightBorderColor: 'border-cyan-200',
    textColor: 'text-accent-cyan',
    lightTextColor: 'text-cyan-600',
    headerGradient: 'from-accent-cyan/20 to-accent-blue/20',
    lightHeaderGradient: 'from-cyan-100 to-blue-100',
  },
  planned: {
    label: 'Planned',
    icon: Target,
    color: 'from-accent-purple to-accent-pink',
    bgColor: 'bg-accent-purple/10',
    lightBgColor: 'bg-purple-50',
    borderColor: 'border-accent-purple/30',
    lightBorderColor: 'border-purple-200',
    textColor: 'text-accent-purple',
    lightTextColor: 'text-purple-600',
    headerGradient: 'from-accent-purple/20 to-accent-pink/20',
    lightHeaderGradient: 'from-purple-100 to-pink-100',
  },
  consideration: {
    label: 'Under Consideration',
    icon: Lightbulb,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-500/10',
    lightBgColor: 'bg-amber-50',
    borderColor: 'border-amber-500/30',
    lightBorderColor: 'border-amber-200',
    textColor: 'text-amber-400',
    lightTextColor: 'text-amber-600',
    headerGradient: 'from-amber-500/20 to-orange-500/20',
    lightHeaderGradient: 'from-amber-100 to-orange-100',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    color: 'from-accent-green to-emerald-500',
    bgColor: 'bg-accent-green/10',
    lightBgColor: 'bg-green-50',
    borderColor: 'border-accent-green/30',
    lightBorderColor: 'border-green-200',
    textColor: 'text-accent-green',
    lightTextColor: 'text-green-600',
    headerGradient: 'from-accent-green/20 to-emerald-500/20',
    lightHeaderGradient: 'from-green-100 to-emerald-100',
  },
};

const categoryConfig = {
  feature: { label: 'Feature', icon: Sparkles, color: 'text-accent-purple', lightColor: 'text-purple-600' },
  improvement: { label: 'Improvement', icon: Zap, color: 'text-accent-cyan', lightColor: 'text-cyan-600' },
  bug_fix: { label: 'Bug Fix', icon: Bug, color: 'text-red-400', lightColor: 'text-red-500' },
  integration: { label: 'Integration', icon: Puzzle, color: 'text-accent-green', lightColor: 'text-green-600' },
};

export default function Roadmap() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [roadmap, setRoadmap] = useState<GroupedRoadmap | null>(null);
  const [votedIds, setVotedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [votingId, setVotingId] = useState<number | null>(null);

  // Request form state
  const [requestForm, setRequestForm] = useState({
    title: '',
    description: '',
    category: 'feature',
    email: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Issue report form state
  const [reportForm, setReportForm] = useState({
    title: '',
    description: '',
    email: '',
  });
  const [reportImage, setReportImage] = useState<File | null>(null);
  const [reportImagePreview, setReportImagePreview] = useState<string | null>(null);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchRoadmap = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/roadmap`);
      if (!response.ok) throw new Error('Failed to fetch roadmap');
      const data: ApiResponse<{ grouped: GroupedRoadmap }> = await response.json();
      if (data.success && data.data) {
        setRoadmap(data.data.grouped);
      }
    } catch (err) {
      setError('Failed to load roadmap');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVotes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/roadmap/my-votes`);
      if (response.ok) {
        const data: ApiResponse<number[]> = await response.json();
        if (data.success && data.data) {
          setVotedIds(data.data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch votes:', err);
    }
  }, []);

  useEffect(() => {
    fetchRoadmap();
    fetchVotes();
  }, [fetchRoadmap, fetchVotes]);

  const handleVote = async (itemId: number) => {
    setVotingId(itemId);
    try {
      const response = await fetch(`${API_URL}/roadmap/${itemId}/vote`, {
        method: 'POST',
      });
      const data: ApiResponse<{ voted: boolean; votes_count: number }> = await response.json();
      if (data.success && data.data) {
        if (data.data.voted) {
          setVotedIds([...votedIds, itemId]);
        } else {
          setVotedIds(votedIds.filter((id) => id !== itemId));
        }
        if (roadmap) {
          const newVotesCount = data.data.votes_count;
          const updateItems = (items: RoadmapItem[]) =>
            items.map((item) =>
              item.id === itemId ? { ...item, votes_count: newVotesCount } : item
            );
          setRoadmap({
            in_progress: updateItems(roadmap.in_progress),
            planned: updateItems(roadmap.planned),
            consideration: updateItems(roadmap.consideration),
            completed: updateItems(roadmap.completed),
          });
        }
      }
    } catch (err) {
      console.error('Vote error:', err);
    } finally {
      setVotingId(null);
    }
  };

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(`${API_URL}/roadmap/feature-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestForm),
      });
      const data: ApiResponse<null> = await response.json();
      if (data.success) {
        setSubmitSuccess(true);
        setRequestForm({ title: '', description: '', category: 'feature', email: '' });
        setTimeout(() => {
          setShowRequestModal(false);
          setSubmitSuccess(false);
        }, 2000);
      } else {
        setSubmitError(data.error || 'Failed to submit request');
      }
    } catch {
      setSubmitError('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setReportError('Image must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setReportError('Please upload an image file');
        return;
      }
      setReportImage(file);
      setReportError(null);
      const reader = new FileReader();
      reader.onload = (e) => {
        setReportImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setReportImage(null);
    setReportImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmitReport = async (e: React.FormEvent) => {
    e.preventDefault();
    setReportSubmitting(true);
    setReportError(null);

    try {
      const formData = new FormData();
      formData.append('title', reportForm.title);
      formData.append('description', reportForm.description);
      formData.append('email', reportForm.email);
      if (reportImage) {
        formData.append('image', reportImage);
      }

      const response = await fetch(`${API_URL}/roadmap/issue-reports`, {
        method: 'POST',
        body: formData,
      });
      const data: ApiResponse<null> = await response.json();
      if (data.success) {
        setReportSuccess(true);
        setReportForm({ title: '', description: '', email: '' });
        setReportImage(null);
        setReportImagePreview(null);
        setTimeout(() => {
          setShowReportModal(false);
          setReportSuccess(false);
        }, 2000);
      } else {
        setReportError(data.error || 'Failed to submit report');
      }
    } catch {
      setReportError('Failed to submit report. Please try again.');
    } finally {
      setReportSubmitting(false);
    }
  };

  const RoadmapCard = ({ item }: { item: RoadmapItem }) => {
    const hasVoted = votedIds.includes(item.id);
    const isVoting = votingId === item.id;
    const category = categoryConfig[item.category];
    const CategoryIcon = category.icon;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`group p-4 rounded-xl border transition-all ${
          isDark
            ? 'bg-dark-800/50 border-white/5 hover:border-white/10'
            : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'
        }`}
      >
        <div className="flex gap-3">
          {/* Vote Button */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => handleVote(item.id)}
              disabled={isVoting || item.status === 'completed'}
              className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center transition-all ${
                hasVoted
                  ? 'bg-gradient-to-br from-accent-purple to-accent-cyan text-white'
                  : isDark
                  ? 'bg-dark-700 hover:bg-dark-600 text-gray-400 hover:text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700'
              } ${item.status === 'completed' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isVoting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <ChevronUp className="w-3 h-3" />
                  <span className="text-xs font-bold">{item.votes_count}</span>
                </>
              )}
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3
                className={`font-semibold text-sm ${
                  isDark ? 'text-white' : 'text-slate-900'
                } ${item.status === 'completed' ? 'line-through opacity-75' : ''}`}
              >
                {item.title}
              </h3>
            </div>
            <span
              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mb-2 ${
                isDark ? 'bg-dark-700' : 'bg-slate-100'
              } ${isDark ? category.color : category.lightColor}`}
            >
              <CategoryIcon className="w-3 h-3" />
              {category.label}
            </span>
            {item.description && (
              <p
                className={`text-xs ${
                  isDark ? 'text-gray-400' : 'text-slate-600'
                } line-clamp-2`}
              >
                {item.description}
              </p>
            )}
            {item.completed_at && (
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                Completed {new Date(item.completed_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const StatusColumn = ({
    status,
    items,
  }: {
    status: keyof typeof statusConfig;
    items: RoadmapItem[];
  }) => {
    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <div className={`rounded-2xl border overflow-hidden ${
        isDark
          ? 'bg-dark-900/50 border-white/5'
          : 'bg-slate-50 border-slate-200'
      }`}>
        {/* Column Header */}
        <div
          className={`flex items-center gap-2 p-4 bg-gradient-to-r ${
            isDark ? config.headerGradient : config.lightHeaderGradient
          }`}
        >
          <div
            className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.color} flex items-center justify-center shadow-lg`}
          >
            <Icon className="w-4 h-4 text-white" />
          </div>
          <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {config.label}
          </span>
          <span
            className={`ml-auto px-2.5 py-1 rounded-full text-xs font-medium ${
              isDark ? 'bg-white/10 text-gray-300' : 'bg-white text-slate-600'
            }`}
          >
            {items.length}
          </span>
        </div>

        {/* Cards Container */}
        <div className="p-3 space-y-3 max-h-[500px] overflow-y-auto scrollbar-thin">
          {items.length === 0 ? (
            <div
              className={`p-4 rounded-xl border border-dashed text-center text-sm ${
                isDark ? 'border-white/10 text-gray-500' : 'border-slate-300 text-slate-400'
              }`}
            >
              No items yet
            </div>
          ) : (
            items.map((item) => <RoadmapCard key={item.id} item={item} />)
          )}
        </div>
      </div>
    );
  };

  return (
    <StaticPageLayout
      title="Product Roadmap"
      subtitle="See what we're working on and vote for features you want to see next."
      fullWidth
    >
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <button
          onClick={() => setShowRequestModal(true)}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-accent-purple to-accent-cyan text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow w-full sm:w-auto"
        >
          <MessageSquarePlus className="w-5 h-5" />
          Request a Feature
        </button>
        <button
          onClick={() => setShowReportModal(true)}
          className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all w-full sm:w-auto ${
            isDark
              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
              : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
          }`}
        >
          <AlertTriangle className="w-5 h-5" />
          Report an Issue
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent-purple" />
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className={`text-center py-20 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <p>{error}</p>
          <button
            onClick={fetchRoadmap}
            className="mt-4 px-4 py-2 bg-accent-purple text-white rounded-lg hover:bg-purple-600"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Roadmap Grid - Vertical Kanban Layout */}
      {!loading && roadmap && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatusColumn status="in_progress" items={roadmap.in_progress} />
          <StatusColumn status="planned" items={roadmap.planned} />
          <StatusColumn status="consideration" items={roadmap.consideration} />
          <StatusColumn status="completed" items={roadmap.completed} />
        </div>
      )}

      {/* Vote Legend */}
      {!loading && roadmap && (
        <div
          className={`mt-8 p-4 rounded-xl border ${
            isDark ? 'bg-dark-800/50 border-white/5' : 'bg-slate-50 border-slate-200'
          }`}
        >
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className={isDark ? 'text-gray-400' : 'text-slate-500'}>
              <ChevronUp className="w-4 h-4 inline mr-1" />
              Click to vote for features you want to see
            </span>
            <span className={isDark ? 'text-gray-500' : 'text-slate-400'}>|</span>
            <span className={isDark ? 'text-gray-400' : 'text-slate-500'}>
              Most voted features rise to the top
            </span>
            <span className={isDark ? 'text-gray-500' : 'text-slate-400'}>|</span>
            <span className={isDark ? 'text-gray-400' : 'text-slate-500'}>
              1 vote per person per feature
            </span>
          </div>
        </div>
      )}

      {/* Feature Request Modal */}
      <AnimatePresence>
        {showRequestModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRequestModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-lg rounded-2xl p-6 ${
                isDark ? 'bg-dark-800 border border-white/10' : 'bg-white shadow-xl'
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center">
                    <MessageSquarePlus className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3
                      className={`text-lg font-semibold ${
                        isDark ? 'text-white' : 'text-slate-900'
                      }`}
                    >
                      Request a Feature
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Tell us what you'd like to see
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRequestModal(false)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-100 text-slate-400'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {submitSuccess ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-8"
                >
                  <div className="w-16 h-16 rounded-full bg-accent-green/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-accent-green" />
                  </div>
                  <h4
                    className={`text-lg font-semibold mb-2 ${
                      isDark ? 'text-white' : 'text-slate-900'
                    }`}
                  >
                    Request Submitted!
                  </h4>
                  <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>
                    Thank you! Our team will review your suggestion.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmitRequest} className="space-y-4">
                  {submitError && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
                      {submitError}
                    </div>
                  )}

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-slate-700'
                      }`}
                    >
                      Title *
                    </label>
                    <input
                      type="text"
                      value={requestForm.title}
                      onChange={(e) =>
                        setRequestForm({ ...requestForm, title: e.target.value })
                      }
                      required
                      maxLength={200}
                      placeholder="Brief description of the feature"
                      className={`w-full h-11 px-4 rounded-xl border focus:outline-none focus:ring-2 focus:ring-accent-purple/50 ${
                        isDark
                          ? 'bg-dark-700 border-white/10 text-white placeholder-gray-500'
                          : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-slate-700'
                      }`}
                    >
                      Category
                    </label>
                    <select
                      value={requestForm.category}
                      onChange={(e) =>
                        setRequestForm({ ...requestForm, category: e.target.value })
                      }
                      className={`w-full h-11 px-4 rounded-xl border focus:outline-none focus:ring-2 focus:ring-accent-purple/50 ${
                        isDark
                          ? 'bg-dark-700 border-white/10 text-white'
                          : 'bg-slate-50 border-slate-200 text-slate-900'
                      }`}
                    >
                      <option value="feature">New Feature</option>
                      <option value="improvement">Improvement</option>
                      <option value="bug_fix">Bug Fix</option>
                      <option value="integration">Integration</option>
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-slate-700'
                      }`}
                    >
                      Description
                    </label>
                    <textarea
                      value={requestForm.description}
                      onChange={(e) =>
                        setRequestForm({ ...requestForm, description: e.target.value })
                      }
                      maxLength={2000}
                      rows={4}
                      placeholder="Describe the feature in detail..."
                      className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-accent-purple/50 resize-none ${
                        isDark
                          ? 'bg-dark-700 border-white/10 text-white placeholder-gray-500'
                          : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-slate-700'
                      }`}
                    >
                      Email (optional)
                    </label>
                    <input
                      type="email"
                      value={requestForm.email}
                      onChange={(e) =>
                        setRequestForm({ ...requestForm, email: e.target.value })
                      }
                      placeholder="Get notified when we ship this"
                      className={`w-full h-11 px-4 rounded-xl border focus:outline-none focus:ring-2 focus:ring-accent-purple/50 ${
                        isDark
                          ? 'bg-dark-700 border-white/10 text-white placeholder-gray-500'
                          : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                      }`}
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowRequestModal(false)}
                      className={`flex-1 h-11 rounded-xl font-medium transition-colors ${
                        isDark
                          ? 'bg-dark-700 text-gray-300 hover:bg-dark-600'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !requestForm.title.trim()}
                      className="flex-1 h-11 rounded-xl bg-gradient-to-r from-accent-purple to-accent-cyan text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Submit Request
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Issue Report Modal */}
      <AnimatePresence>
        {showReportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowReportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${
                isDark ? 'bg-dark-800 border border-white/10' : 'bg-white shadow-xl'
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3
                      className={`text-lg font-semibold ${
                        isDark ? 'text-white' : 'text-slate-900'
                      }`}
                    >
                      Report an Issue
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Help us fix bugs and problems
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReportModal(false)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-slate-100 text-slate-400'
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {reportSuccess ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-8"
                >
                  <div className="w-16 h-16 rounded-full bg-accent-green/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-accent-green" />
                  </div>
                  <h4
                    className={`text-lg font-semibold mb-2 ${
                      isDark ? 'text-white' : 'text-slate-900'
                    }`}
                  >
                    Report Submitted!
                  </h4>
                  <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>
                    Thank you! We'll investigate this issue.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmitReport} className="space-y-4">
                  {reportError && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
                      {reportError}
                    </div>
                  )}

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-slate-700'
                      }`}
                    >
                      Issue Title *
                    </label>
                    <input
                      type="text"
                      value={reportForm.title}
                      onChange={(e) =>
                        setReportForm({ ...reportForm, title: e.target.value })
                      }
                      required
                      maxLength={200}
                      placeholder="Brief description of the issue"
                      className={`w-full h-11 px-4 rounded-xl border focus:outline-none focus:ring-2 focus:ring-red-500/50 ${
                        isDark
                          ? 'bg-dark-700 border-white/10 text-white placeholder-gray-500'
                          : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-slate-700'
                      }`}
                    >
                      Description *
                    </label>
                    <textarea
                      value={reportForm.description}
                      onChange={(e) =>
                        setReportForm({ ...reportForm, description: e.target.value })
                      }
                      required
                      maxLength={2000}
                      rows={4}
                      placeholder="What happened? What did you expect to happen?"
                      className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-red-500/50 resize-none ${
                        isDark
                          ? 'bg-dark-700 border-white/10 text-white placeholder-gray-500'
                          : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                      }`}
                    />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-slate-700'
                      }`}
                    >
                      Screenshot (optional)
                    </label>

                    {reportImagePreview ? (
                      <div className="relative">
                        <img
                          src={reportImagePreview}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-xl border border-white/10"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute top-2 right-2 p-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                          isDark
                            ? 'border-white/10 hover:border-white/20'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Upload className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                          Click to upload a screenshot
                        </p>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                          PNG, JPG up to 5MB
                        </p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-gray-300' : 'text-slate-700'
                      }`}
                    >
                      Email (optional)
                    </label>
                    <input
                      type="email"
                      value={reportForm.email}
                      onChange={(e) =>
                        setReportForm({ ...reportForm, email: e.target.value })
                      }
                      placeholder="Get notified when we fix this"
                      className={`w-full h-11 px-4 rounded-xl border focus:outline-none focus:ring-2 focus:ring-red-500/50 ${
                        isDark
                          ? 'bg-dark-700 border-white/10 text-white placeholder-gray-500'
                          : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
                      }`}
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowReportModal(false)}
                      className={`flex-1 h-11 rounded-xl font-medium transition-colors ${
                        isDark
                          ? 'bg-dark-700 text-gray-300 hover:bg-dark-600'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={reportSubmitting || !reportForm.title.trim() || !reportForm.description.trim()}
                      className="flex-1 h-11 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white font-medium shadow-lg shadow-red-500/25 hover:shadow-red-500/40 transition-shadow disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {reportSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Submit Report
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </StaticPageLayout>
  );
}
